import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Hold (pointer or Space/Enter) for `durationMs` to trigger. Releasing early cancels. */
export function useHoldToTrigger(durationMs: number, onTrigger: () => void) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const holdingRef = useRef(false);
  const triggerRef = useRef(onTrigger);
  triggerRef.current = onTrigger;

  const cancel = useCallback(() => {
    holdingRef.current = false;
    cancelAnimationFrame(rafRef.current);
    setProgress(0);
  }, []);

  const start = useCallback(() => {
    if (holdingRef.current) return;
    holdingRef.current = true;
    startRef.current = performance.now();
    const tick = () => {
      if (!holdingRef.current) return;
      const p = Math.min(1, (performance.now() - startRef.current) / durationMs);
      setProgress(p);
      if (p >= 1) {
        holdingRef.current = false;
        setProgress(0);
        triggerRef.current();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [durationMs]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const handlers = {
    onPointerDown: () => start(),
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
        e.preventDefault();
        start();
      }
    },
    onKeyUp: (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') cancel();
    },
  };

  return { progress, handlers };
}
